/**
 * The Azure resources behind this deployment and what they cost per month.
 *
 * This is fixed platform cost, not consumption, and it does not move with
 * usage — which is exactly why it belongs behind a tab rather than on the
 * overview. It is worth keeping because Xtract also ships as a managed
 * application into a customer's own subscription, where these lines land on
 * their Azure bill.
 */
import { Info, Server } from 'lucide-react'
import { formatBytes, formatInt, formatUsdAs } from '../../utils/billing'

const CATEGORY_LABELS = {
  compute: 'Compute',
  database: 'Database',
  storage: 'Storage',
  ai: 'AI & ML',
  monitoring: 'Monitoring',
  messaging: 'Messaging',
  identity: 'Identity',
  other: 'Other',
}

export default function InfrastructurePanel({ infra, displayCurrency }) {
  if (!infra) return null

  const services = infra.services || {}
  const storage = infra.storage_metrics || {}

  const grouped = Object.entries(services).reduce((acc, [key, svc]) => {
    const cat = svc.category || 'other'
    ;(acc[cat] = acc[cat] || []).push({ key, ...svc })
    return acc
  }, {})

  const ordered = Object.entries(grouped).sort(
    (a, b) =>
      b[1].reduce((s, x) => s + (x.monthly_usd || 0), 0) -
      a[1].reduce((s, x) => s + (x.monthly_usd || 0), 0),
  )

  const fmt = (usd, digits) => formatUsdAs(usd, displayCurrency, digits)

  const summary = [
    { label: 'Storage used', value: formatBytes(storage.total_bytes) },
    { label: 'Documents stored', value: formatInt(storage.total_documents) },
    { label: 'Active services', value: formatInt(Object.keys(services).length) },
  ]

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Server className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Platform infrastructure</h2>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">
              Monthly estimate
            </span>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Fixed running cost of the Azure resources behind this deployment. It does not change with usage.
          </p>
        </div>
        <p className="text-xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
          {fmt(infra.monthly_total_usd)}
          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">/mo</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {summary.map(item => (
          <div key={item.label} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {item.label}
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums mt-0.5">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {ordered.map(([cat, svcs], groupIndex) => {
          const catTotal = svcs.reduce((sum, s) => sum + (s.monthly_usd || 0), 0)
          return (
            <div key={cat} className="animate-line-in" style={{ animationDelay: `${groupIndex * 45}ms` }}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {CATEGORY_LABELS[cat] || cat}
                </p>
                <p className="text-[11px] font-semibold text-gray-600 dark:text-gray-300 tabular-nums">
                  {fmt(catTotal)}/mo
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                {svcs.map(svc => (
                  <div key={svc.key} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">{svc.name}</p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{svc.description}</p>
                    </div>
                    <span className="text-[13px] font-semibold tabular-nums text-gray-700 dark:text-gray-200 flex-shrink-0">
                      {svc.monthly_usd > 0 ? fmt(svc.monthly_usd) : 'Free'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {infra.note} Usage-driven services are estimated separately under Consumption.
        </p>
      </div>
    </div>
  )
}
